/**
 * CGM-OPERATIONAL-CONTROL-R3 — parcel-level semantic valve controls.
 *
 * The canonical binding is the only physical authority. Telemetry age does not
 * block explicit OPEN/CLOSE. Missing commissioning proof,
 * a busy command or an uncertain outcome still disables both actions.
 */
import ParcelSupervision from './ParcelSupervision';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { canIssuePhysicalCommands } from '../auth/cognito';
import type { IoTNode, ParcelValveBinding } from '../lib/iotApi';
import {
  parcelValveState,
  parcelMayBePowerSaving,
  parcelControlAvailability,
  type ParcelValveState,
} from '../lib/parcelOperational';
import { isBusyStatus } from '../lib/valveCommandSafety';
import type { ParcelCommandOutcome } from '../hooks/useParcelValveCommand';

interface SafeParcelControlProps {
  parcels: ParcelValveBinding[];
  displayNameFor?: (binding: ParcelValveBinding) => string;
  nodes: IoTNode[];
  now: number;
  canCommand?: boolean;
  onCommand?: (binding: ParcelValveBinding, action: 'open' | 'close') => void;
  statusFor: (bindingId: string) => string | undefined;
  outcomeFor: (bindingId: string) => ParcelCommandOutcome;
}

const SafeParcelControl: React.FC<SafeParcelControlProps> = ({
  parcels, displayNameFor, nodes, now, canCommand, onCommand, statusFor, outcomeFor,
}) => {
  const { t } = useTranslation('agrisafeR1');
  const { user } = useAuth();
  // Defense in depth: pages may pass a stricter local gate, but omission can
  // never turn into authority. The component resolves the canonical role gate.
  const roleCanCommand = canIssuePhysicalCommands(user?.role);
  const effectiveCanCommand = canCommand === undefined
    ? roleCanCommand
    : canCommand && roleCanCommand;
  const nodeById = new Map(nodes.map((node) => [node.id, node]));

  const stateLabel = (state: ParcelValveState): string => {
    switch (state) {
      case 'open': return t('parcel.state.open');
      case 'closed': return t('parcel.state.closed');
      case 'stale': return t('parcel.state.stale');
      default: return t('parcel.state.unknown');
    }
  };

  const lockLabel = (binding: ParcelValveBinding): string | null => {
    if (binding.command_authorized) return null;
    switch (binding.lock_reason) {
      case 'BINDING_NOT_CONFIRMED': return t('parcel.lockReason.BINDING_NOT_CONFIRMED');
      case 'COMMISSIONING_NOT_COMPLETE': return t('parcel.lockReason.COMMISSIONING_NOT_COMPLETE');
      case 'ACTUATION_NOT_AUTHORIZED': return t('parcel.lockReason.ACTUATION_NOT_AUTHORIZED');
      case 'PHYSICAL_INSTALLATION_NOT_CONFIRMED':
      case 'WIRING_NOT_CONFIRMED':
      case 'LOCAL_TEST_NOT_OK':
      case 'REMOTE_TEST_NOT_OK':
        return t('pumping.diagnostic.channelPending');
      default: return t('parcel.locked');
    }
  };

  const outcomeLabel = (outcome: ParcelCommandOutcome): string | null => {
    switch (outcome) {
      case 'unassigned': return t('sectorDetail.notAssigned');
      case 'semantic_binding_required': return t('dashboard.irrigationMappingToConfirm');
      case 'commissioning_incomplete': return t('parcel.lockReason.COMMISSIONING_NOT_COMPLETE');
      case 'actuation_not_authorized': return t('parcel.lockReason.ACTUATION_NOT_AUTHORIZED');
      case 'wiring_not_confirmed': return t('pumping.diagnostic.channelPending');
      case 'conflict': return t('parcel.status.conflict');
      case 'forbidden': return t('parcel.status.forbidden');
      case 'uncertain': return t('parcel.status.uncertain');
      default: return null;
    }
  };

  return (
    <section
      className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 shadow-sm"
      data-testid="parcel-controls"
      aria-label={t('parcel.title')}
    >
      <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('parcel.title')}</h3>
      {parcels.length === 0 ? (
        <p className="mt-2 text-sm text-[var(--text-secondary)]" data-testid="parcel-none">
          {t('sectorDetail.notAssigned')}
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {parcels.map((binding) => {
            const node = nodeById.get(binding.controller_node_id);
            const state = parcelValveState(node, binding.channel_id, now);
            const rawStatus = statusFor(binding.binding_id);
            const busy = isBusyStatus(rawStatus);
            const outcome = outcomeFor(binding.binding_id);
            const authorized = binding.command_authorized;
            const controlAuthorized = authorized && effectiveCanCommand && outcome === null;
            const { canOpen, canClose } = parcelControlAvailability(state, controlAuthorized, busy);
            const statusText = busy
              ? t('parcel.status.busy')
              : outcomeLabel(outcome)
                ?? (rawStatus === 'OBSERVED_FAILED'
                  ? t('sectorDetail.commandState.failed')
                  : rawStatus === 'TIMEOUT'
                  ? t('parcel.status.timeout')
                  : rawStatus === 'UNCERTAIN'
                    ? t('parcel.status.uncertain')
                    : (rawStatus === 'OBSERVED_SUCCESS' && state === 'unknown'
                      ? t('parcel.status.unconfirmed')
                      : null));

            return (
              <li
                key={binding.binding_id}
                data-testid={`parcel-${binding.binding_id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[var(--border-light)] bg-[var(--bg-tertiary)] px-3 py-2 text-sm"
              >
                <ParcelSupervision data={nodeById.get(binding.controller_node_id)?.parcel_supervision?.[binding.binding_id]} />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium text-[var(--text-primary)]" data-testid={`parcel-name-${binding.binding_id}`}>
                    {displayNameFor?.(binding) ?? binding.parcel}
                  </span>
                  <span className="truncate text-xs text-[var(--text-secondary)]" data-testid={`parcel-node-${binding.binding_id}`}>
                    {binding.controller_node_id} · {binding.channel_id}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs font-medium uppercase text-[var(--text-secondary)]" data-testid={`parcel-state-${binding.binding_id}`}>
                    {statusText ?? (parcelMayBePowerSaving(binding, node, state)
                      ? t('parcel.state.powerSavingPossible') : stateLabel(state))}
                  </span>
                  <button type="button" onClick={() => canOpen && onCommand?.(binding, 'open')} disabled={!canOpen}
                    data-testid={`parcel-open-${binding.binding_id}`}
                    className="inline-flex min-h-[40px] items-center justify-center rounded-lg bg-[var(--color-success)] px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40">
                    {t('parcel.open')}
                  </button>
                  <button type="button" onClick={() => canClose && onCommand?.(binding, 'close')} disabled={!canClose}
                    data-testid={`parcel-close-${binding.binding_id}`}
                    className="inline-flex min-h-[40px] items-center justify-center rounded-lg bg-[var(--text-secondary)] px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40">
                    {t('parcel.close')}
                  </button>
                </span>
                {!authorized && lockLabel(binding) && (
                  <span className="w-full text-xs text-[var(--color-warning)]" data-testid={`parcel-lock-${binding.binding_id}`}>
                    {lockLabel(binding)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default SafeParcelControl;
